import { FC, useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertIcon, AlertTitle, Avatar, Box, Button, Flex, Skeleton, Text } from '@chakra-ui/react';
import { getAssessmentById, unlock, findWords } from './answer-assessment-service';
import { useParams } from 'react-router-dom';
import { IAssessment, API, Headers, calculateAge } from '@bemap/utils';
import { Container, GridContainer, HeaderText, IconContainer, OptionText, PageWrapper, ProfileEmail, ProfileName, SwitchButton } from './answer-assessment-styles';
import { Logo } from '@bemap/ui';
import { MdCheckCircleOutline, MdLockOpen } from 'react-icons/md';
import { LoadingScreen } from '@bemap/ui';
import { IWord, openUrl, useLanguage } from '@bemap/utils';
import { useTranslation } from 'react-i18next';

interface Question {
  id: number;
  name: string;
  value: boolean;
}

interface FormMetadata {
  inner: string[];
  env: string[];
  timer: {
    start: number;
    end: number | null;
  };
}

const AnswerAssessment: FC = () => {
  const { hash } = useParams()
  const { t } = useTranslation()
  const { updateLanguage } = useLanguage()

  const initialAssessment: IAssessment = {
    team_id: 0,
    report_id: 1,
    profile: {
      name: t('answer_assessment.loading'),
      email: t('answer_assessment.loading')
    }
  }

  const [assessment, setAssessment] = useState<IAssessment>(initialAssessment);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [answered, setAnswered] = useState<boolean>(false);
  const [unlocking, setUnlocking] = useState<boolean>(false);
  const [logo, setLogo] = useState<string>('');

  const [optionsInner, setOptionsInner] = useState<Question[]>([]);
  const [optionsEnv, setOptionsEnv] = useState<Question[]>([]);

  const [stage, setStage] = useState(0);
  const [form, _] = useState<FormMetadata>({
    inner: [],
    env: [],
    timer: {
      start: Date.now(),
      end: null,
    },
  });

  type LanguageKey = 'pt-BR' | 'en-US' | 'es-ES';
  type WordKey = 'word' | 'word_en' | 'word_es';

  const LANGUAGE_WORD_MAP: Record<LanguageKey, WordKey> = {
    'pt-BR': 'word',
    'en-US': 'word_en',
    'es-ES': 'word_es'
  } as const;

  function shuffleArray(array: IWord[]): Question[] {
    // Create a copy of the array to avoid mutating the original
    return array
      .map((item, index) => ({
        item,
        sort: Math.random(),
        index // Preserve original index for stable sort
      }))
      .sort((a, b) => a.sort - b.sort || a.index - b.index) // Ensure stable sort
      .map(({ item }) => ({
        id: item.order,
        name: item[LANGUAGE_WORD_MAP[assessment.team?.language as LanguageKey] ?? 'word'],
        value: false
      }));
  }

  function randomizeTwice(words: IWord[]) {
    const inner = shuffleArray(words);
    const env = shuffleArray(words);

    setOptionsInner(inner);
    setOptionsEnv(env);
  }

  useEffect(() => {
    loadAssessment();
  }, []);

  useEffect(() => {
    if (assessment?.id) {
      loadWords();
    }
  }, [assessment?.id]);

  async function loadAssessment() {
    try {
      setIsLoading(true);
      const assessmentResponse = await getAssessmentById(hash || '');
      setAssessment(assessmentResponse.message?.assessment);
      setAnswered(assessmentResponse.message?.answered);
      setLogo(assessmentResponse.message?.logo);
      updateLanguage(assessmentResponse.message?.language)
      setIsLoading(false);
    } catch (error) {
      setAssessment(initialAssessment);
    }
  }

  async function loadWords() {
    try {
      setIsLoading(true);
      const wordsResponse = await findWords();
      randomizeTwice(wordsResponse.message);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsSaving(false);
    }
  }

  const handleToggleInner = (id: number) => {
    setOptionsInner((prevState) =>
      prevState.map((option) =>
        option.id === id ? { ...option, value: !option.value } : option
      )
    );
  };

  const handleToggleEnv = (id: number) => {
    setOptionsEnv((prevState) =>
      prevState.map((option) =>
        option.id === id ? { ...option, value: !option.value } : option
      )
    );
  };

  const nextStep = async () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setStage((prevStage) => prevStage + 1);
  };

  const previousStep = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setStage((prevStage) => prevStage - 1);
  };

  const finish = async () => {
    const inner = optionsInner.filter((option) => option.value).map((option) => option.id);
    const env = optionsEnv.filter((option) => option.value).map((option) => option.id);
    setIsSaving(true);
    try {
      await API.post(`/assessments/${hash}/answer`, {
        inner,
        env,
        timer: {
          start: form?.timer?.start,
          end: Date.now()
        }
      }, await Headers.normal());
      setIsSaving(false);
      setStage(3)
    } catch (err) {
      setIsSaving(false);
      console.log(err)
    }
  }

  async function unlockAsClient() {
    setUnlocking(true);
    try {
      const response = await unlock(hash || '');
      openUrl(response.message)
      setUnlocking(false);
    } catch (error) {
      setUnlocking(false);
    }
  }

  const enableButton = () => {
    switch (stage) {
      case 1:
        return optionsInner.filter(option => option.value).length >= import.meta.env.VITE_MIN_ANSWERS
      case 2:
        return optionsEnv.filter(option => option.value).length >= import.meta.env.VITE_MIN_ANSWERS
      default:
        return false
    }
  }

  const RenderButtonSteps = () => {
    return (
      <Flex direction={'row'} gap="20px" mt='20px'>
        <Button variant='outline' onClick={previousStep} disabled={stage === 0}>
          {t('answer_assessment.back')}
        </Button>
        <Button onClick={stage === 2 ? finish : nextStep} colorScheme={'blue'} isDisabled={!enableButton()} isLoading={isSaving}>
          {stage === 2 ? t('answer_assessment.finish') : t('answer_assessment.next')}
        </Button>
      </Flex>
    )
  }

  const RenderStage = (stg: number) => {
    switch (stg) {
      case 0:
        return (
          <>
            <Flex mt='20px' direction={'row'} gap='20px' justifyContent={'center'} alignItems={'center'}>
              <Avatar
                _hover={{ cursor: 'pointer' }}
                color='blue.700'
                name={assessment.profile?.name}
                bg='blue.100'
                size='lg'
              />
              <Flex direction={'column'} alignItems={'flex-start'}>
                <ProfileName>{assessment.profile?.name} {assessment.profile?.metadata?.birth && (`(${calculateAge(assessment.profile.metadata?.birth)})`)}</ProfileName>
                <Skeleton minH={'20px'} isLoaded={!isLoading}>
                  {assessment.profile?.phone && (<ProfileEmail>{assessment.profile?.phone}</ProfileEmail>)}
                  <ProfileEmail>{assessment.profile?.email}</ProfileEmail>
                </Skeleton>
              </Flex>
            </Flex>

            <Text align={'center'}>{t('answer_assessment.step_0')}</Text>

            <Button mt='20px' alignSelf={'center'} colorScheme={'blue'} onClick={nextStep} isDisabled={isLoading}>{t('answer_assessment.init')}</Button>
          </>
        ) // STAGE 0
      case 1:
        return (
          <>
            <HeaderText>
              {t('answer_assessment.step_1')}
            </HeaderText>

            <GridContainer id='content'>
              {optionsInner.map((option) => (
                <SwitchButton
                  key={option.id}
                  checked={option.value}
                  onClick={() => handleToggleInner(option.id)}
                >
                  <IconContainer>
                    {option.value && <MdCheckCircleOutline color='#4299e1' aria-hidden="true" />}
                  </IconContainer>
                  <OptionText>{option.name}</OptionText>
                </SwitchButton>
              ))}
            </GridContainer>

            {RenderButtonSteps()}
          </>
        ) // STAGE 1
      case 2:
        return (
          <>
            <HeaderText>
              {t('answer_assessment.step_2')}
            </HeaderText>

            <GridContainer id='content'>
              {optionsEnv.map((option) => (
                <SwitchButton
                  key={option.id}
                  checked={option.value}
                  onClick={() => handleToggleEnv(option.id)}
                >
                  <IconContainer>
                    {option.value && <MdCheckCircleOutline color='#4299e1' aria-hidden="true" />}
                  </IconContainer>
                  <OptionText>{option.name}</OptionText>
                </SwitchButton>
              ))}
            </GridContainer>

            {RenderButtonSteps()}
          </>
        ) // STAGE 2
      case 3:
        return (
          <>
            <Alert
              status='success'
              variant='subtle'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
              textAlign='center'
              height='200px'
            >
              <AlertIcon boxSize='40px' mr={0} />
              <AlertTitle mt={4} mb={1} fontSize='lg'>
                {t('answer_assessment.form_sent')}
              </AlertTitle>
              <AlertDescription maxWidth='sm'>
                {t('answer_assessment.thanks_text')}
              </AlertDescription>
            </Alert>
            {assessment.client_can_unlock && (
              <Button mt="20px" leftIcon={<MdLockOpen />} onClick={unlockAsClient} variant='solid' colorScheme={'blue'} isLoading={unlocking} isDisabled={!assessment.client_can_unlock}>
                {t('answer_assessment.unlock')}
              </Button>
            )}
          </>
        )
      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <Box h={'100vh'} w={'100vw'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
        <LoadingScreen />
      </Box>
    )
  }

  return (
    <PageWrapper>
      <Container>
        <Flex w='100%' alignItems={'center'} justifyContent={'center'}>
          <Logo url={logo} />
        </Flex>
        {assessment && !answered ? (
          <>
            {RenderStage(stage)}
          </>
        ) : (
          <Flex mt='20px' justifyContent={'center'} fontSize={'1.3rem'}>
            {t('answer_assessment.answered')}
          </Flex>
        )}
        {assessment.status === 'done' && assessment.client_can_unlock && answered && (
          <Button leftIcon={<MdLockOpen />} onClick={unlockAsClient} variant='solid' colorScheme={'blue'} isLoading={isSaving} disabled={!assessment.client_can_unlock}>
            {t('answer_assessment.unlock')}
          </Button>
        )}
      </Container>
    </PageWrapper>
  )
};

export default AnswerAssessment;
